import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components';
import { useModal } from '@/components/common/modal/ModalContext';
import { shareQuoteId } from '@/services/BtsService';

/**
 * ShareQuoteModal
 * 
 * Opens inside the existing ModalContext.
 * Shows all emails for a proposal as removable chips (temporary — no DB change).
 * On "Share Quote", calls POST /api/school-proposals/{id}/share-quote with
 * only the remaining (selected) emails.
 */
const ShareQuoteModal = ({ proposalId, emailList = [], onSuccess }) => {
  const { closeModal } = useModal();
  const [selectedEmails, setSelectedEmails] = useState(
    emailList.filter(Boolean),
  );
  const [loading, setLoading] = useState(false);

  const removeEmail = (index) => {
    setSelectedEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleShare = async () => {
    if (selectedEmails.length === 0) return;
    setLoading(true);
    try {
      const result = await shareQuoteId(proposalId, selectedEmails);
      closeModal();
      if (onSuccess) onSuccess(result);
    } catch (err) {
      // error toast handled by shareQuoteId service
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-2 min-w-[420px]">
      {/* Email chips */}
      {selectedEmails.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 mb-8">
          {selectedEmails.map((email, index) => (
            <span
              key={index}
              className="flex items-center gap-3 bg-gray-100 text-gray-800 text-base rounded-xl px-5 py-3"
            >
              {email}
              <span
                onClick={() => removeEmail(index)}
                className="text-gray-400 text-lg cursor-pointer select-none leading-none"
                title="Remove"
              >
                ×
              </span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm mb-8 text-center">
          No emails to share with. Add emails to the proposal first.
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-4 justify-center mt-2">
        <Button
          type="button"
          onClick={closeModal}
          className="secondary"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleShare}
          disabled={selectedEmails.length === 0 || loading}
          className="primary"
        >
          {loading ? 'Sharing...' : 'Share Quote'}
        </Button>
      </div>
    </div>
  );
};

ShareQuoteModal.propTypes = {
  proposalId: PropTypes.number.isRequired,
  emailList: PropTypes.arrayOf(PropTypes.string),
  onSuccess: PropTypes.func,
};

export default ShareQuoteModal;
